import { cloneDeep, isEqual } from 'lodash/fp';

import { ExternalIntegrationDataSource, Table } from 'dashboard/models/external-integration';
import { LabelBackgrounds } from 'design-system/components/Sticker';
import { ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import { Iso8601DateTimeString } from 'lib/types';

export enum ImporterArchiveAfterCompletion {
  True = 'true',
  False = 'false',
}

export enum ImporterSyncMode {
  Upsert = 'upsert',
  Insert = 'insert',
  Update = 'update',
}

export enum ImporterArraySyncMode {
  Append = 'append',
  Replace = 'replace',
}

export enum ImporterStatus {
  Active = 'active',
  Inactive = 'inactive',
  Archived = 'archived',
}

export const isImporterActive = (importer: Importer): boolean => {
  return importer.attributes.status === ImporterStatus.Active;
};

export const IMPORTER_STATUS_TO_LABEL: Record<ImporterStatus, string> = {
  [ImporterStatus.Active]: 'Active',
  [ImporterStatus.Inactive]: 'Inactive',
  [ImporterStatus.Archived]: 'Archived',
};

export const IMPORTER_STATUS_TO_VARIANT: Record<ImporterStatus, LabelBackgrounds> = {
  [ImporterStatus.Active]: 'green',
  [ImporterStatus.Inactive]: 'yellow',
  [ImporterStatus.Archived]: 'pink',
};

export type ImporterType =
  | ApiModel.ImporterPipelineCsv
  | ApiModel.ImporterPipelineGoogleSheets
  | ApiModel.ImporterPipelineMysql
  | ApiModel.ImporterPipelineRedshift
  | ApiModel.ImporterPipelineSnowflake
  | ApiModel.ImporterPipelineS3;

export interface ImporterPipelineConfigCursorComponent {
  'field-name': string;
  direction: string;
}

export interface ImporterPipelineConfigCursor {
  components: ImporterPipelineConfigCursorComponent[];
}

export interface ImporterPipelineConfigSchedule {
  type: 'interval_seconds';
  interval_seconds: number;
}

export interface ImporterPipelineConfig {
  'base-query'?: string;
  'cursor-order'?: ImporterPipelineConfigCursor;
  destination?: 'Account' | 'Txn';
  'destination-type-id'?: string;
  schedule?: ImporterPipelineConfigSchedule;
  'source-dest-column-mapping'?: { [key: string]: string };
  'table-name'?: string;
  'sync-mode'?: ImporterSyncMode;
  'array-sync-mode'?: ImporterArraySyncMode;
  'archive-after-completion'?: boolean;
}

export enum ImporterRunType {
  Manual = 'manual',
  Recurring = 'recurring',
}

// used for any FE input components that requires a state for manual scheduling
export const MANUAL_SCHEDULE_STATE = 'manual_schedule';

export interface ImporterAttributes {
  status: ImporterStatus;
  name: string;
  'pipeline-config': ImporterPipelineConfig;
  'archived-at': Iso8601DateTimeString;
  'created-at': Iso8601DateTimeString;
  'last-synced-at': Iso8601DateTimeString;
  'updated-at': Iso8601DateTimeString;
  'next-run-at': Iso8601DateTimeString;
  'run-type': ImporterRunType;
}

export const IMPORTER_TYPES = {
  [ApiModel.ImporterPipelineCsv]: 'CSV',
  [ApiModel.ImporterPipelineGoogleSheets]: 'Google Sheets',
  [ApiModel.ImporterPipelineMysql]: 'MySQL',
  [ApiModel.ImporterPipelineSnowflake]: 'Snowflake',
  [ApiModel.ImporterPipelineRedshift]: 'Redshift',
  [ApiModel.ImporterPipelineS3]: 'S3',
};

export const IMPORTER_TYPES_TO_DATA_SOURCE_TYPES = {
  [ApiModel.ImporterPipelineCsv]: [ApiModel.ExternalIntegrationDataSourceCsv],
  [ApiModel.ImporterPipelineMysql]: [ApiModel.ExternalIntegrationDataSourceMysql],
  [ApiModel.ImporterPipelineSnowflake]: [ApiModel.ExternalIntegrationDataSourceSnowflake],
  [ApiModel.ImporterPipelineRedshift]: [ApiModel.ExternalIntegrationDataSourceRedshift],
  [ApiModel.ImporterPipelineS3]: [ApiModel.ExternalIntegrationDataSourceS3],
  [ApiModel.ImporterPipelineGoogleSheets]: [ApiModel.ExternalIntegrationDataSourceGoogleSheets],
};

export const RECURRABLE_IMPORTERS = [
  ApiModel.ImporterPipelineMysql,
  ApiModel.ImporterPipelineSnowflake,
  ApiModel.ImporterPipelineRedshift,
];

export const QUERYABLE_IMPORTERS = [
  ApiModel.ImporterPipelineCsv,
  ApiModel.ImporterPipelineMysql,
  ApiModel.ImporterPipelineSnowflake,
  ApiModel.ImporterPipelineRedshift,
  ApiModel.ImporterPipelineS3,
];

export interface ImporterRelationships {
  'external-integration':
    | ApiRelationship<ApiModel.ExternalIntegrationDataSourceCsv>
    | ApiRelationship<ApiModel.ExternalIntegrationDataSourceGoogleSheets>
    | ApiRelationship<ApiModel.ExternalIntegrationDataSourceMongodb>
    | ApiRelationship<ApiModel.ExternalIntegrationDataSourceMysql>
    | ApiRelationship<ApiModel.ExternalIntegrationDataSourcePostgres>
    | ApiRelationship<ApiModel.ExternalIntegrationDataSourceSnowflake>
    | ApiRelationship<ApiModel.ExternalIntegrationDataSourceS3>;
}

export type Importer = ApiResource<ImporterAttributes, ImporterRelationships, ImporterType>;

export function isTableSchemaEqual(
  dataSource1: ExternalIntegrationDataSource | undefined,
  dataSource2: ExternalIntegrationDataSource | undefined,
) {
  // Given array of tables, sorts tables by name, maps tables to data struct of columns with just name and type, and sorts each table's columns by name.
  const generateTableSchema = (tables: Table[]) => {
    tables = cloneDeep(tables);
    return tables
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((table) => {
        return {
          name: table.name,
          columns: table.columns
            .map((col) => {
              return { name: col.name, type: col.type };
            })
            .sort((a, b) => a.name.localeCompare(b.name)),
        };
      });
  };

  if (
    !dataSource1?.attributes?.schema?.tables?.length ||
    !dataSource2?.attributes?.schema?.tables?.length ||
    dataSource1.attributes.schema.tables.length !== dataSource2.attributes.schema.tables.length
  ) {
    return false;
  }

  const oldTableSchema = generateTableSchema(dataSource1.attributes.schema.tables);
  const newTableSchema = generateTableSchema(dataSource2.attributes.schema.tables);

  return isEqual(oldTableSchema, newTableSchema);
}
